import { Badge, type BadgeTone, cn } from "@norican/react";

import { type LabelledTier, useEnvironmentTier } from "@/hooks";
import { useT } from "@/i18n";

/** Solid, token-backed colour per tier: amber UAT, blue STG, inverse demo. */
const TIER_CLASSES: Record<LabelledTier, string> = {
  UAT: "bg-warning text-warning-fg-on-solid",
  STG: "bg-info text-info-fg-on-solid",
  TEST: "bg-warning text-warning-fg-on-solid",
  DEMO: "bg-bg-inverse text-fg-on-inverse",
};

const TIER_TONES: Record<LabelledTier, BadgeTone> = {
  UAT: "warning",
  STG: "info",
  TEST: "warning",
  DEMO: "neutral",
};

/**
 * Full-width strip across the top of the sticky header on non-LIVE tiers.
 *
 * Lives inside the header so it stays on screen on a long page, and renders
 * nothing on LIVE.
 *
 * DS gap: the design system has no page-level banner. `Alert` is an inline,
 * subtle callout whose warning tone is `role="alert"`, which would be
 * announced on every navigation. Until one exists, this is a plain strip on
 * the solid tier tokens.
 */
export function EnvironmentBanner() {
  const { t } = useT();
  const tier = useEnvironmentTier();
  if (!tier) {
    return null;
  }

  return (
    <div
      role="note"
      aria-label={t("environment.label")}
      className={cn("w-full", TIER_CLASSES[tier])}
    >
      <div className="mx-auto flex w-full max-w-[var(--norican-size-container-xl)] items-center gap-3 px-4 py-1.5 sm:px-6 lg:px-8">
        <span className="shrink-0 text-lg font-extrabold tracking-widest">{tier}</span>
        <span className="min-w-0 text-sm font-medium">
          {tier === "DEMO" ? t("environment.demo") : t("environment.nonLive", { tier })}
        </span>
      </div>
    </div>
  );
}

/**
 * The tier beside the logo, so it reads as part of the brand mark. Hidden on
 * phones, where the header has no room for it and the strip already says it.
 */
export function EnvironmentTag() {
  const tier = useEnvironmentTier();
  if (!tier) {
    return null;
  }

  return (
    <Badge
      aria-hidden
      tone={TIER_TONES[tier]}
      variant="solid"
      shape="rounded"
      className="hidden font-extrabold tracking-widest sm:inline-flex"
    >
      {tier}
    </Badge>
  );
}
